import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getMarketReportBySlug, type MarketReport } from "@/lib/market-reports.functions";
import { seo, type SanityPostMeta } from "@/lib/seo";
import { breadcrumb, article, portableTextWordCount } from "@/lib/structured-data";
import { schemaImages, FALLBACK_SCHEMA_IMAGES, type SchemaImage } from "@/lib/sanity-image";
import { PortableText } from "@/lib/portable-text";
import { NotFoundPage } from "@/components/not-found-page";
import { SITE_URL } from "@/lib/seo-constants";
import { urlFor } from "@/lib/sanity";

const TAG_CLASS =
  "inline-flex items-center rounded-full border border-pink-soft/40 bg-pink-soft/15 px-3 py-1 text-xs font-semibold tracking-wide text-pink-100";

function formatSgt(iso: string): string {
  try {
    const d = new Date(iso);
    const datePart = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Singapore",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(d);
    const timePart = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Singapore",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
    return `${datePart} – ${timePart} SGT`;
  } catch {
    return "";
  }
}

function resolveHeroImage(report: MarketReport): string | null {
  if (report.heroImage?.asset?.url) {
    try {
      return urlFor(report.heroImage as Parameters<typeof urlFor>[0])
        .width(1600)
        .height(900)
        .fit("crop")
        .auto("format")
        .url();
    } catch {
      return report.heroImage.asset.url;
    }
  }
  return report.heroImageUrl ?? null;
}

export const Route = createFileRoute("/market-reports/$slug")({
  loader: async ({ params }): Promise<MarketReport> => {
    const report = await getMarketReportBySlug({ data: { slug: params.slug } });
    if (!report) throw notFound();
    return report;
  },
  head: ({ loaderData, params }) => {
    const path = `/market-reports/${params.slug}`;
    if (!loaderData) {
      return seo({
        title: "Report not found",
        description: "The market report you're looking for doesn't exist.",
        path,
        noindex: true,
      });
    }
    const report = loaderData as MarketReport;
    const hasBody = Array.isArray(report.content) && report.content.length > 0;
    const slug = report.slug?.current ?? params.slug;
    const heroAssetUrl = report.heroImage?.asset?.url;
    const ogAssetUrl = report.og_image?.asset?.url;

    // Sanity OG/Twitter overrides. heroImage maps to coverImage so the seo()
    // helper picks it up as the default social card. For mock fallbacks there is
    // no Sanity asset, so we pass the imported image path via `image` instead.
    const sanityMeta: SanityPostMeta = {
      title: report.title,
      excerpt: report.summary,
      publishedAt: report.publishedAt,
      _updatedAt: report._updatedAt,
      meta_title: report.meta_title,
      meta_description: report.meta_description,
      keywords: report.keywords,
      canonical_url: report.canonical_url,
      og_title: report.og_title,
      og_description: report.og_description,
      og_image: ogAssetUrl ? (report.og_image as SanityPostMeta["og_image"]) : undefined,
      twitter_title: report.twitter_title,
      twitter_description: report.twitter_description,
      twitter_card_type: report.twitter_card_type,
      coverImage: heroAssetUrl ? (report.heroImage as SanityPostMeta["coverImage"]) : undefined,
      authorName: report.authorName,
    };

    // Article `image`: the Sanity hero (dereferenced to `asset.url` by the GROQ
    // projection) in the three crops Google asks for, else the mock's local asset,
    // else the site-wide OG card.
    const images: SchemaImage[] = heroAssetUrl
      ? schemaImages(report.heroImage as Parameters<typeof schemaImages>[0])
      : report.heroImageUrl
        ? [
            {
              "@type": "ImageObject",
              url: `${SITE_URL}${report.heroImageUrl}`,
              width: 1200,
              height: 630,
            },
          ]
        : FALLBACK_SCHEMA_IMAGES;

    const jsonLd = [
      breadcrumb([
        { name: "Home", url: "/" },
        { name: "Market Reports", url: "/market-reports" },
        { name: report.title, url: `/market-reports/${slug}` },
      ]),
      article({
        headline: report.title,
        description: report.summary,
        url: `${SITE_URL}/market-reports/${slug}`,
        image: images,
        datePublished: report.publishedAt,
        dateModified: report._updatedAt,
        authorName: report.authorName,
        articleSection: report.industryTag,
        wordCount: portableTextWordCount(report.content),
        keywords: report.keywords,
        type: "NewsArticle",
      }),
    ];

    return seo({
      title: report.title,
      description: report.summary ?? "",
      path,
      type: "article",
      noindex: !hasBody,
      image: heroAssetUrl ? undefined : report.heroImageUrl,
      publishedTime: report.publishedAt,
      modifiedTime: report._updatedAt,
      authorName: report.authorName,
      sanityMeta,
      jsonLd,
    });
  },
  notFoundComponent: NotFoundPage,
  component: MarketReportPage,
});

function MarketReportPage() {
  const report = Route.useLoaderData() as MarketReport;
  const hasBody = Array.isArray(report.content) && report.content.length > 0;
  const imgUrl = resolveHeroImage(report);

  return (
    <article className="max-w-3xl mx-auto px-6 py-16 text-white">
      <nav className="text-sm text-white/50 mb-8 flex items-center gap-2">
        <Link to="/" className="hover:text-white transition-colors">
          Home
        </Link>
        <span aria-hidden>›</span>
        <Link to="/market-reports" className="hover:text-white transition-colors">
          Market Reports
        </Link>
        <span aria-hidden>›</span>
        <span className="text-white/80 truncate">{report.title}</span>
      </nav>

      <div className="mb-4">
        <span className={TAG_CLASS}>{report.industryTag}</span>
      </div>

      <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6 leading-tight">
        {report.title}
      </h1>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/50 mb-8">
        <span>
          By{" "}
          {report.authorSocialUrl ? (
            <a
              href={report.authorSocialUrl}
              target="_blank"
              rel="author noopener noreferrer"
              className="font-medium text-white/80 hover:text-pink-soft underline-offset-2 hover:underline transition-colors"
            >
              {report.authorName}
            </a>
          ) : (
            <span className="font-medium text-white/80">{report.authorName}</span>
          )}
        </span>
        <span aria-hidden>·</span>
        <time dateTime={report.publishedAt} className="tabular-nums">
          Published {formatSgt(report.publishedAt)}
        </time>
      </div>

      {imgUrl && (
        <img
          src={imgUrl}
          alt={report.title}
          className="w-full rounded-lg object-cover mb-10 aspect-video"
          width={1600}
          height={900}
          loading="eager"
        />
      )}

      {hasBody ? (
        <PortableText value={report.content} className="space-y-0" />
      ) : report.summary ? (
        <p className="text-lg text-white/75 leading-relaxed">{report.summary}</p>
      ) : null}

      <div className="mt-16 pt-8 border-t border-white/10">
        <Link
          to="/market-reports"
          className="text-sm text-pink-soft hover:text-white transition-colors"
        >
          ← Back to Market Reports
        </Link>
      </div>
    </article>
  );
}
